import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { startOfMonth, endOfMonth, eachDayOfInterval, format, isSameDay, isSameMonth, startOfWeek, endOfWeek, addMonths, subMonths } from "date-fns";
import { es } from 'date-fns/locale';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { Building2, MapPin, Box, ClipboardList, Bell, Clock, DollarSign, Plus, QrCode, ChevronLeft, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { AppleButton } from "@/components/ui/apple-button";
import { Button } from "@/components/ui/button";
import { MiniReminders } from "@/components/layout/MiniReminders";
import { cn } from "@/lib/utils";
import { Tooltip as TooltipUI, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useState, useEffect } from "react";
import { CreateReminderForm } from "@/components/forms/CreateReminderForm";
import { useNotifications } from "@/hooks/useNotifications";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";
import { MiniRecords } from "@/components/layout/MiniRecords";
import { Map, Marker, Popup } from '@vis.gl/react-maplibre';
import { AssetsMap, BasicAsset } from "@/components/maps/AssetsMap";
import { Database } from "@/integrations/supabase/types";

type Tables = Database['public']['Tables'];
type AssetRow = Tables['assets']['Row'];

type MapAsset = Pick<AssetRow, 'id' | 'name' | 'asset_name' | 'latitude' | 'longitude'>;

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

export default function Overview() {
  const { t, i18n } = useTranslation();
  const { todayRecords, todayReminders } = useNotifications();
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const navigate = useNavigate();

  // Get the current locale for date-fns
  const dateLocale = i18n.language.startsWith('es') ? es : undefined;

  // Translate weekdays based on current locale
  const weekDays = React.useMemo(() => {
    return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => 
      t(`calendar.weekdays.${day.toLowerCase()}`)
    );
  }, [t]);

  // Fetch overview data and all reminders for the calendar
  const { data: overviewData, refetch: refetchOverview } = useQuery({
    queryKey: ["overview"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const [
        { count: assetsCount },
        { count: placesCount },
        { count: companiesCount },
        { count: recordsCount },
        { count: remindersCount },
        { data: assetsByCategory },
        { data: recordsByType },
        { data: assetsByStatus },
        { data: recentRecords },
        { data: recentReminders },
        { data: assetValues },
        { data: allReminders },
      ] = await Promise.all([
        supabase.from("assets").select("*", { count: "exact", head: true }).eq("user_id", user.id),
        supabase.from("places").select("*", { count: "exact", head: true }).eq("user_id", user.id),
        supabase.from("companies").select("*", { count: "exact", head: true }).eq("user_id", user.id),
        supabase.from("asset_records").select("*", { count: "exact", head: true }).eq("user_id", user.id),
        supabase.from("reminders").select("*", { count: "exact", head: true }).eq("user_id", user.id),
        supabase
          .from("assets")
          .select("category")
          .eq("user_id", user.id)
          .not("category", "is", null),
        supabase
          .from("asset_records")
          .select("type")
          .eq("user_id", user.id)
          .not("type", "is", null),
        supabase
          .from("assets")
          .select("status")
          .eq("user_id", user.id)
          .not("status", "is", null),
        supabase
          .from("asset_records")
          .select(`
            *,
            assets:asset_id (
              id,
              name,
              asset_name
            )
          `)
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(5),
        supabase
          .from("reminders")
          .select(`
            *,
            assets:asset_id (
              id,
              name,
              asset_name
            )
          `)
          .eq("user_id", user.id)
          .order("next_trigger_at", { ascending: true })
          .limit(5),
        supabase
          .from("assets")
          .select("category, asset_price")
          .eq("user_id", user.id)
          .not("category", "is", null)
          .not("asset_price", "is", null),
        supabase
          .from("reminders")
          .select(`
            *,
            assets:asset_id (
              id,
              name,
              asset_name
            )
          `)
          .eq("user_id", user.id)
          .order("next_trigger_at", { ascending: true }),
      ]);

      // Process category distribution with translations
      const categoryDistribution = assetsByCategory?.reduce((acc: any, { category }) => {
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {});

      const categoryData = Object.entries(categoryDistribution || {}).map(([name, value]) => ({
        name: t(`forms.asset.categories.${name.toLowerCase()}`) || name,
        originalName: name,
        value,
      }));

      // Process record types distribution with translations
      const recordTypeDistribution = recordsByType?.reduce((acc: any, { type }) => {
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});

      const recordTypeData = Object.entries(recordTypeDistribution || {}).map(([name, value]) => ({
        name: t(`forms.record.types.${name.toLowerCase()}`) || name,
        originalName: name,
        value,
      }));

      // Process status distribution with translations
      const statusDistribution = assetsByStatus?.reduce((acc: any, { status }) => {
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      const statusData = Object.entries(statusDistribution || {}).map(([name, value]) => ({
        name: t(`forms.asset.status.${name.toLowerCase()}`) || name,
        originalName: name,
        value,
      }));

      // Process asset values by category with translations
      const valueDistribution = assetValues?.reduce((acc: any, { category, asset_price }) => {
        acc[category] = (acc[category] || 0) + Number(asset_price);
        return acc;
      }, {});

      const valueData = Object.entries(valueDistribution || {}).map(([name, value]) => ({
        name: t(`forms.asset.categories.${name.toLowerCase()}`) || name,
        originalName: name,
        value: Number(value),
      }));

      return {
        assetsCount: assetsCount || 0,
        placesCount: placesCount || 0,
        companiesCount: companiesCount || 0,
        recordsCount: recordsCount || 0,
        remindersCount: remindersCount || 0,
        categoryData,
        recordTypeData,
        statusData,
        recentRecords,
        recentReminders,
        valueData,
        allReminders,
      };
    },
  });

  // Add userAssets query with debugging
  const { data: userAssets } = useQuery<BasicAsset[]>({
    queryKey: ['userAssets'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      console.log('Fetching assets for user:', user.id);
      
      // First get assets owned by the user
      const { data: ownedAssets, error: ownedError } = await supabase
        .from('assets')
        .select(`
          id,
          name,
          asset_name,
          latitude,
          longitude,
          category,
          status,
          company:companies (
            id,
            name,
            description
          ),
          place:places (
            id,
            name,
            description
          )
        `)
        .eq('user_id', user.id)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .neq('latitude', 0)
        .neq('longitude', 0)
        .or('private.eq.false,private.is.null');

      if (ownedError) {
        console.error('Error fetching owned assets:', ownedError);
        throw ownedError;
      }
      
      console.log('Owned assets:', ownedAssets);

      // Then get assets shared with the user that they can see
      const { data: sharedAssets, error: sharedError } = await supabase
        .from('assets')
        .select(`
          id,
          name,
          asset_name,
          latitude,
          longitude,
          category,
          status,
          company:companies (
            id,
            name,
            description
          ),
          place:places (
            id,
            name,
            description
          ),
          shared_assets!inner (
            shared_with
          )
        `)
        .eq('shared_assets.shared_with', user.id)
        .eq('see_info', true)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .neq('latitude', 0)
        .neq('longitude', 0)
        .or('private.eq.false,private.is.null');

      if (sharedError) {
        console.error('Error fetching shared assets:', sharedError);
        throw sharedError;
      }
      
      console.log('Shared assets:', sharedAssets);

      // Combine and deduplicate the results
      const allAssets = [
        ...(ownedAssets || []).map(asset => ({
          ...asset,
          company: asset.company?.[0],
          place: asset.place?.[0]
        })), 
        ...(sharedAssets?.map(({ shared_assets, ...asset }) => ({
          ...asset,
          company: asset.company?.[0],
          place: asset.place?.[0]
        })) || [])
      ] as BasicAsset[];
      
      // Use Set to deduplicate by id
      const seen = new Set<number>();
      const uniqueAssets = allAssets.filter(asset => {
        if (seen.has(asset.id)) {
          return false;
        }
        seen.add(asset.id);
        return true;
      });
      
      console.log('Final unique assets with coordinates:', uniqueAssets);
      return uniqueAssets;
    }
  });

  // Add new query for all assets (including those without coordinates)
  const { data: allUserAssets } = useQuery({
    queryKey: ['allUserAssets'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get all owned assets
      const { data: ownedAssets, error: ownedError } = await supabase
        .from('assets')
        .select(`
          id,
          name,
          asset_name,
          category,
          status
        `)
        .eq('user_id', user.id)
        .or('private.eq.false,private.is.null');

      if (ownedError) throw ownedError;

      // Get all shared assets
      const { data: sharedAssets, error: sharedError } = await supabase
        .from('assets')
        .select(`
          id,
          name,
          asset_name,
          category,
          status,
          shared_assets!inner (
            shared_with
          )
        `)
        .eq('shared_assets.shared_with', user.id)
        .eq('see_info', true)
        .or('private.eq.false,private.is.null');

      if (sharedError) throw sharedError;

      // Combine and deduplicate
      const allAssets = [
        ...(ownedAssets || []),
        ...(sharedAssets?.map(({ shared_assets, ...asset }) => asset) || [])
      ];

      const seen = new Set<number>();
      return allAssets.filter(asset => {
        if (seen.has(asset.id)) return false;
        seen.add(asset.id);
        return true;
      });
    }
  });

  // Add queryClient for manual cache invalidation
  const queryClient = useQueryClient();

  // Add debug useEffect
  useEffect(() => {
    if (userAssets) {
      console.log('All assets:', userAssets);
      console.log('Assets with coordinates:', userAssets.filter(asset => asset.latitude && asset.longitude));
    }
  }, [userAssets]);

  if (!overviewData) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  // Function to get reminders for a specific date
  const getRemindersForDate = (date: Date) => {
    return overviewData.allReminders.filter((reminder: any) => 
      isSameDay(new Date(reminder.next_trigger_at), date)
    );
  };

  // Get calendar days
  const getDaysInMonth = (date: Date) => {
    const start = startOfWeek(startOfMonth(date));
    const end = endOfWeek(endOfMonth(date));
    return eachDayOfInterval({ start, end });
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="space-y-6">
      {/* Header with Quick Actions */}
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold tracking-tight">{t("overview.title")}</h1>
          <LanguageSwitcher />
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <AppleButton
            variant="secondary"
            size="sm"
            onClick={() => window.location.href = '/assets/new'}
            className="justify-center bg-white hover:bg-gray-50/75"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("assets.newAsset")}
          </AppleButton>
          {userAssets?.length > 0 && (
            <AppleButton
              variant="secondary"
              size="sm"
              onClick={() => setShowReminderDialog(true)}
              className="justify-center bg-white hover:bg-gray-50/75"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("reminders.new")}
            </AppleButton>
          )}
          <AppleButton
            variant="secondary"
            size="sm"
            onClick={() => window.location.href = '/scan'}
            className="justify-center bg-white hover:bg-gray-50/75"
          >
            <QrCode className="h-4 w-4 mr-2" />
            {t("menu.scan")}
          </AppleButton>
        </div>
      </div>

      {/* Summary Cards - Always at top */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Link to="/assets" className="block">
          <Card className="p-4 flex items-center space-x-4 order-2 sm:order-2 hover:bg-gray-50 transition-colors cursor-pointer bg-white">
            <div className="p-3 rounded-full bg-blue-100">
              <Box className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t("overview.totalAssets")}</p>
              <p className="text-2xl font-semibold">{overviewData.assetsCount}</p>
            </div>
          </Card>
        </Link>

        <Link to="/places" className="block">
          <Card className="p-4 flex items-center space-x-4 order-3 sm:order-3 hover:bg-gray-50 transition-colors cursor-pointer bg-white">
            <div className="p-3 rounded-full bg-green-100">
              <MapPin className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t("overview.totalPlaces")}</p>
              <p className="text-2xl font-semibold">{overviewData.placesCount}</p>
            </div>
          </Card>
        </Link>

        <Link to="/companies" className="block">
          <Card className="p-4 flex items-center space-x-4 order-4 sm:order-4 hover:bg-gray-50 transition-colors cursor-pointer bg-white">
            <div className="p-3 rounded-full bg-yellow-100">
              <Building2 className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t("overview.totalCompanies")}</p>
              <p className="text-2xl font-semibold">{overviewData.companiesCount}</p>
            </div>
          </Card>
        </Link>

        <Link to="/records" className="block">
          <Card className="p-4 flex items-center space-x-4 order-5 sm:order-5 hover:bg-gray-50 transition-colors cursor-pointer bg-white">
            <div className="p-3 rounded-full bg-purple-100">
              <ClipboardList className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t("overview.totalRecords")}</p>
              <p className="text-2xl font-semibold">{overviewData.recordsCount}</p>
            </div>
          </Card>
        </Link>

        <Link to="/records?tab=reminders" className="block col-span-2 sm:col-span-1">
          <Card className="p-4 flex items-center space-x-4 order-1 sm:order-1 hover:bg-gray-50 transition-colors cursor-pointer bg-white">
            <div className="p-3 rounded-full bg-red-100">
              <Bell className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t("overview.totalReminders")}</p>
              <p className="text-2xl font-semibold">{overviewData.remindersCount}</p>
            </div>
          </Card>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Recent Records and Reminders */}
        <div className="lg:col-span-4 space-y-6 order-1">
            {/* Mini Records */}
              <Card className="overflow-hidden bg-white">
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">{t("records.recentRecords")}</h2>
                  </div>
                </div>
                <MiniRecords onRecordChange={refetchOverview} />
              </Card>

              {/* Mini Reminders */}
              <Card className="overflow-hidden bg-white">
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">{t("reminders.title")}</h2>
                  </div>
                </div>
                <MiniReminders onReminderChange={refetchOverview} />
              </Card>

            {/* Calendar */}
          <Card className="p-4 bg-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">{t("reminders.title")}</h2>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                    className="hidden sm:flex"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium">
                    {format(currentDate, 'MMMM yyyy', { locale: dateLocale })}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                    className="hidden sm:flex"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="rounded-lg overflow-hidden bg-white">
                {/* Month Navigation for Mobile */}
                <div className="flex justify-between items-center mb-4 sm:hidden">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentDate(subMonths(currentDate, 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-[2px] mb-[2px]">
                  {weekDays.map((day) => (
                    <div
                      key={day}
                      className="h-8 flex items-center justify-center text-[11px] sm:text-sm font-medium text-gray-500"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-[2px]">
                  {days.map((day) => {
                    const reminders = getRemindersForDate(day);
                    const hasReminders = reminders.length > 0;
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const hasHighPriority = reminders.some((r: any) => r.priority === 'high');
                    const hasMediumPriority = reminders.some((r: any) => r.priority === 'medium');
                    const isSelected = selectedDay ? isSameDay(day, selectedDay) : false;

                    return (
                      <TooltipProvider key={day.toString()}>
                        <TooltipUI open={isSelected || undefined}>
                          <TooltipTrigger asChild>
                            <div
                              onClick={() => setSelectedDay(isSelected ? null : day)}
                              className={cn(
                                "h-9 sm:h-12 p-1 relative flex flex-col items-center cursor-pointer",
                                "hover:bg-gray-100/50 transition-colors rounded-sm",
                                !isCurrentMonth && "opacity-30",
                                isSameDay(day, new Date()) && "bg-blue-50",
                                isSelected && "bg-gray-100",
                                hasHighPriority && "bg-red-50",
                                !hasHighPriority && hasMediumPriority && "bg-yellow-50",
                                !hasHighPriority && !hasMediumPriority && hasReminders && "bg-blue-50"
                              )}
                            >
                              <span className={cn(
                                "text-xs sm:text-sm",
                                !isCurrentMonth && "text-gray-400",
                                hasHighPriority && "text-red-600 font-medium",
                                !hasHighPriority && hasMediumPriority && "text-yellow-600 font-medium",
                                !hasHighPriority && !hasMediumPriority && hasReminders && "text-blue-600 font-medium"
                              )}>
                                {format(day, 'd')}
                              </span>
                              {hasReminders && (
                                <div className="absolute bottom-0.5 sm:bottom-1 flex gap-[2px] items-center justify-center">
                                  {reminders.slice(0, 3).map((reminder: any, i) => (
                                    <div
                                      key={i}
                                      className={cn(
                                        "h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full",
                                        reminder.priority === 'high' ? "bg-red-500" :
                                        reminder.priority === 'medium' ? "bg-yellow-500" :
                                        "bg-blue-500"
                                      )}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent 
                            className="bg-white border shadow-lg p-2 z-50 max-w-[280px] sm:max-w-none" 
                            onPointerDownOutside={() => setSelectedDay(null)}
                          >
                            <div className="space-y-3">
                              {hasReminders && (
                                <div className="space-y-2">
                                  {reminders.map((reminder: any) => (
                                    <div key={reminder.id} className="text-xs bg-white">
                                      <span className={cn(
                                        "font-medium",
                                        reminder.priority === 'high' ? "text-red-500" :
                                        reminder.priority === 'medium' ? "text-yellow-500" :
                                        "text-blue-500"
                                      )}>
                                        {reminder.title}
                                      </span>
                                      <span className="text-gray-600 ml-1">
                                        ({reminder.assets?.asset_name || reminder.assets?.name})
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className={cn("pt-2", hasReminders && "border-t")}>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="w-full text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => {
                                    setShowReminderDialog(true);
                                    setSelectedDay(day);
                                  }}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  {t("reminders.new")}
                                </Button>
                              </div>
                            </div>
                          </TooltipContent>
                        </TooltipUI>
                      </TooltipProvider>
                    );
                  })}
                </div>
              </div>

              {/* Legend */}
              <div className="mt-4 flex items-center justify-center gap-3 sm:gap-6 text-[10px] sm:text-xs text-gray-600 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 sm:h-3 sm:w-3 rounded-full bg-red-500" />
                  <span>{t("reminders.priority.high")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 sm:h-3 sm:w-3 rounded-full bg-yellow-500" />
                  <span>{t("reminders.priority.medium")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 sm:h-3 sm:w-3 rounded-full bg-blue-500" />
                  <span>{t("reminders.priority.low")}</span>
                </div>
              </div>
            </Card>
        </div>

        {/* Right Column - Map and Charts */}
        <div className="lg:col-span-8 space-y-6 order-2">
          {/* Assets Map */}
          <Card className="p-6 bg-white">
            <h2 className="text-lg font-semibold mb-4">{t("overview.assetsMap")}</h2>
            <AssetsMap assets={userAssets || []} />
          </Card>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Asset Categories */}
            <Card className="p-6 bg-white">
              <h2 className="text-lg font-semibold mb-4">{t("overview.assetsByCategory")}</h2>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overviewData.categoryData} margin={{ top: 5, right: 20, bottom: 65, left: 0 }}>
                    <defs>
                      <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0088FE" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#0088FE" stopOpacity={0.2}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="originalName" 
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      stroke="#6b7280"
                    />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      formatter={(value: any) => [value, t("overview.charts.totalAssets")]}
                      labelFormatter={(label: string) => label}
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}
                    />
                    <Bar 
                      dataKey="value" 
                      fill="url(#colorBar)"
                      radius={[4, 4, 0, 0]}
                      animationDuration={1500}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Record Types */}
            <Card className="p-6 bg-white">
              <h2 className="text-lg font-semibold mb-4">{t("overview.recordsByType")}</h2>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      {COLORS.map((color, index) => (
                        <linearGradient key={`gradient-${index}`} id={`colorGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={color} stopOpacity={0.8}/>
                          <stop offset="100%" stopColor={color} stopOpacity={0.5}/>
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={overviewData.recordTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ originalName, percent }) => `${originalName} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      innerRadius={60}
                      paddingAngle={2}
                      nameKey="originalName"
                      dataKey="value"
                      animationDuration={1500}
                    >
                      {overviewData.recordTypeData.map((_, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={`url(#colorGradient-${index % COLORS.length})`}
                          stroke="#fff"
                          strokeWidth={1}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: any) => [value, t("overview.charts.totalRecords")]}
                      labelFormatter={(label: string) => label}
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Asset Status */}
            <Card className="p-6 bg-white">
              <h2 className="text-lg font-semibold mb-4">{t("overview.assetsByStatus")}</h2>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <defs>
                      {COLORS.map((color, index) => (
                        <linearGradient key={`statusGradient-${index}`} id={`statusGradient-${index}`} x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor={color} stopOpacity={0.9}/>
                          <stop offset="100%" stopColor={color} stopOpacity={0.6}/>
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={overviewData.statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ originalName, percent }) => percent > 0.05 ? `${originalName} (${(percent * 100).toFixed(0)}%)` : ''}
                      outerRadius={100}
                      innerRadius={70}
                      paddingAngle={4}
                      nameKey="originalName"
                      dataKey="value"
                      animationDuration={1500}
                    >
                      {overviewData.statusData.map((_, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={`url(#statusGradient-${index % COLORS.length})`}
                          stroke="#ffffff"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: any) => [value, t("overview.charts.totalAssets")]}
                      labelFormatter={(label: string) => label}
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Asset Value Distribution */}
            <Card className="p-6 bg-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">{t("overview.assetValueByCategory")}</h2>
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={overviewData.valueData} margin={{ top: 5, right: 20, bottom: 65, left: 0 }}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00C49F" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#00C49F" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorStroke" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#00C49F" stopOpacity={1}/>
                        <stop offset="95%" stopColor="#00C49F" stopOpacity={0.8}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="originalName" 
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      stroke="#6b7280"
                    />
                    <YAxis 
                      tickFormatter={(value) => formatCurrency(value)}
                      stroke="#6b7280"
                    />
                    <Tooltip 
                      formatter={(value: any) => [formatCurrency(Number(value)), t("overview.charts.price")]}
                      labelFormatter={(label: string) => label}
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="url(#colorStroke)" 
                      fill="url(#colorValue)"
                      strokeWidth={2}
                      animationDuration={1500}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Add New Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{t('reminders.createReminder')}</DialogTitle>
              <DialogDescription>
                {t('reminders.createReminderDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {/* First, select an asset */}
              {!selectedAssetId ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">{t('forms.asset.title')}</h3>
                  <div className="grid gap-2">
                    {allUserAssets?.map((asset) => (
                      <Button
                        key={asset.id}
                        variant="outline"
                        className="justify-start"
                        onClick={() => setSelectedAssetId(asset.id)}
                      >
                        {asset.name || asset.asset_name || t('assets.unnamedAsset')}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <CreateReminderForm
                  assetId={selectedAssetId}
                  initialData={{
                    next_trigger_at: selectedDay
                  }}
                  onSuccess={() => {
                    setShowReminderDialog(false);
                    setSelectedAssetId(null);
                    setSelectedDay(null);
                    // Invalidate and refetch all necessary data
                    refetchOverview();
                    queryClient.invalidateQueries({ queryKey: ['miniReminders'] });
                    queryClient.invalidateQueries({ queryKey: ['notifications'] });
                    queryClient.invalidateQueries({ queryKey: ['overview'] });
                    queryClient.invalidateQueries({ queryKey: ['allReminders'] });
                  }}
                  onCancel={() => {
                    setShowReminderDialog(false);
                    setSelectedAssetId(null);
                    setSelectedDay(null);
                  }}
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 